import { useState, useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api, type DashboardStats } from '../services/api';
import { LoadingCard } from '../components/LoadingSpinner';
import { StatsCard } from '../components/StatsCard';
import { LiveCheckIns } from '../components/LiveCheckIns';
import { ManualCheckinModal } from '../components/ManualCheckinModal';
import { QRCheckoutDisplay } from '../components/QRCheckoutDisplay';

const API_BASE = import.meta.env.VITE_API_BASE;

interface Service {
  id: string;
  name: string;
  description: string;
  weekday: number;
  start_time: string;
  duration_minutes: number;
  tags: string[];
  is_active: boolean;
}

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  allergies?: string;
  medicalNotes?: string;
  family: {
    id: string;
    parentName: string;
  };
}

// Global checkins state
const checkinsState = signal<{
  stats: DashboardStats | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}>({
  stats: null,
  isLoading: true,
  error: null,
  lastUpdated: null,
});

export function CheckInsPage() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedService, setSelectedService] = useState<string>('');
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [showManualCheckinModal, setShowManualCheckinModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [children, setChildren] = useState<Child[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Load church services
  const loadServices = async () => {
    try {
      setServicesLoading(true);
      const response = await fetch(`${API_BASE}/church/services`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('church_admin_token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setServices(data.services);
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  // Load families and children for manual check-in
  const loadChildrenForCheckin = async () => {
    setLoadingChildren(true);
    try {
      const data = await api.getFamilies();
      if (data.success) {
        const allChildren: Child[] = [];
        data.families.forEach((family) => {
          family.children.forEach((child) => {
            allChildren.push({
              id: child.id,
              firstName: child.firstName,
              lastName: child.lastName,
              birthdate: child.birthdate,
              allergies: child.allergies,
              medicalNotes: child.medicalNotes,
              family: {
                id: family.id,
                parentName: family.parentName,
              },
            });
          });
        });
        setChildren(allChildren);
      }
    } catch (error) {
      console.error('Failed to load children:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  const handleOpenManualCheckin = async () => {
    setShowManualCheckinModal(true);
    await loadChildrenForCheckin();
  };

  const handleManualCheckinSuccess = () => {
    loadCheckinsData(selectedDate, selectedService);
  };

  // Load checkins data
  const loadCheckinsData = async (date?: string, serviceTime?: string) => {
    try {
      checkinsState.value = { ...checkinsState.value, isLoading: true, error: null };
      const stats = await api.getDashboardStats(date, serviceTime);
      checkinsState.value = {
        stats,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      };
    } catch (error: any) {
      console.error('Failed to load checkins:', error);
      checkinsState.value = {
        ...checkinsState.value,
        isLoading: false,
        error: error.message || 'Failed to load checkins data',
      };
    }
  };

  useEffect(() => {
    loadServices();
    loadCheckinsData(selectedDate, selectedService);

    const interval = setInterval(() => {
      loadCheckinsData(selectedDate, selectedService);
    }, 30000);

    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedDate, selectedService]);

  useEffect(() => {
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [refreshInterval]);

  const handleRefresh = () => {
    loadCheckinsData(selectedDate, selectedService);
  };

  const { stats, isLoading, error, lastUpdated } = checkinsState.value;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '36px 24px 60px', display: 'flex', flexDirection: 'column', gap: 26 }}>
      
      {/* ── Header & Desk Controls (Soft Minimalism) ── */}
      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid rgba(25, 38, 49, 0.08)',
          borderRadius: 20,
          padding: '24px 28px',
          boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p className="mini-label" style={{ margin: 0 }}>Attendance Desk</p>
              {lastUpdated && (
                <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                  · Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: '-0.04em',
                color: 'var(--ink)',
                margin: '2px 0 0',
              }}
            >
              Check-ins Management
            </h1>
          </div>

          {/* Controls Bar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            {/* Date Filter */}
            <input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate((e.target as HTMLInputElement).value)}
              disabled={isLoading}
              style={{
                background: 'rgba(25, 38, 49, 0.03)',
                border: '1px solid rgba(25, 38, 49, 0.1)',
                borderRadius: 12,
                padding: '7px 12px',
                fontSize: 13,
                color: 'var(--ink)',
                fontFamily: 'var(--font-body)',
                outline: 'none',
              }}
            />

            {/* Service Filter */}
            <select
              id="service"
              value={selectedService}
              onChange={(e) => setSelectedService((e.target as HTMLSelectElement).value)}
              disabled={isLoading || servicesLoading}
              style={{
                background: 'rgba(25, 38, 49, 0.03)',
                border: '1px solid rgba(25, 38, 49, 0.1)',
                borderRadius: 12,
                padding: '7px 12px',
                fontSize: 13,
                color: 'var(--ink)',
                fontFamily: 'var(--font-body)',
                outline: 'none',
              }}
            >
              <option value="">All Services</option>
              {services.map((service) => (
                <option key={service.id} value={service.name}>
                  {service.name}
                </option>
              ))}
            </select>

            {/* QR Code Action */}
            <button
              onClick={() => setShowQRModal(true)}
              className="btn-secondary"
              style={{ padding: '7px 14px', fontSize: 12 }}
            >
              QR Code
            </button>

            {/* Manual Check-in Action */}
            <button
              onClick={handleOpenManualCheckin}
              className="btn-primary"
              style={{ padding: '7px 16px', fontSize: 12 }}
              disabled={loadingChildren}
            >
              + Check In
            </button>

            {/* Refresh Action */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="btn-secondary"
              style={{ padding: '7px 12px', fontSize: 12 }}
              title="Refresh attendance data"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert-error" style={{ borderRadius: 14 }}>
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Failed to load check-ins</p>
            <p style={{ fontSize: 13, opacity: 0.8, margin: '2px 0 0' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Loading Skeleton */}
      {isLoading && !stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <LoadingCard /><LoadingCard /><LoadingCard />
          </div>
          <LoadingCard />
        </div>
      )}

      {/* ── Active Check-ins Content ── */}
      {stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} className="animate-slide-up">
          
          {/* ── Top Live Desk Stats: Total Check-ins, Currently Checked In, Checked Out ── */}
          {/* Note: Unique families, Unique children, Avg duration removed per prompt */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <StatsCard
              title="Total Check-ins"
              value={stats.stats.totalCheckins}
              icon="users"
              color="marigold"
            />
            <StatsCard
              title="Currently Checked In"
              value={stats.stats.currentlyCheckedIn}
              icon="check-circle"
              color="leaf"
              pulse={stats.stats.currentlyCheckedIn > 0}
            />
            <StatsCard
              title="Checked Out"
              value={stats.stats.checkedOut}
              icon="log-out"
              color="gray"
            />
          </div>

          {/* ── Live Check-Ins Desk List with Pickup Codes & Check-out Actions ── */}
          {/* Note: Breakdown by age group, service time, and recent activities removed per prompt */}
          <LiveCheckIns date={selectedDate} serviceTime={selectedService} />

        </div>
      )}

      {/* Manual Check-in Modal */}
      {showManualCheckinModal && (
        <ManualCheckinModal
          children={children}
          onClose={() => setShowManualCheckinModal(false)}
          onSuccess={handleManualCheckinSuccess}
        />
      )}

      {/* QR Checkout Display Modal */}
      {showQRModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowQRModal(false);
          }}
        >
          <div
            style={{
              background: 'var(--paper)',
              borderRadius: 24,
              border: '1px solid rgba(25, 38, 49, 0.1)',
              boxShadow: '0 24px 60px rgba(25, 38, 49, 0.15)',
              width: '100%',
              maxWidth: 440,
              overflow: 'hidden',
              margin: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 24px',
                borderBottom: '1px solid rgba(25, 38, 49, 0.08)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--marigold-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#7D5507',
                  }}
                >
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5V16M4 8h4V4H4v4zm12 0h4V4h-4v4zM4 20h4v-4H4v4z" />
                  </svg>
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
                  QR Pickup Display
                </h3>
              </div>
              <button
                onClick={() => setShowQRModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--ink-faint)',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <QRCheckoutDisplay />
          </div>
        </div>
      )}
    </div>
  );
}